import {
  Skeleton,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useContext, useState } from "react";
import { InfoOutlined } from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AuthContext } from "../../context/auth-context";
import { ENTITY_LABELS } from "../../utils/labelMapping";
import { OVERVIEW_LLM_LABELS } from "../../utils/constant";
import BrandAvatar from "../../Component/Avatar/Avatar";
import MUICard from "../../Component/UI/MUICard/MUICard";
import MUIIconButton from "../../Component/UI/MUIIconButton/MUIIconButton";
import Table, { StyledTableCell, StyledTableRow } from "../../Component/UI/Table/Table";
import { extractDomain } from "../../utils/helpers";
import CATabbedModal from "./CATabbedModal";
import CADetailedTableModal from "./CADetailedTableModal";
import classes from "../BrandAudit/CoreAttributes/CoreAttributes.module.css";

function getLLMLabel(llm) {
  return OVERVIEW_LLM_LABELS[llm] || llm || "";
}

function formatName(str) {
  if (!str) return "";
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

// Find attribute data in an attributes object using case/whitespace-insensitive lookup
function findAttrData(attributes, key) {
  if (!attributes) return undefined;
  const trimmedKey = key.trim();
  if (attributes[trimmedKey] !== undefined) return attributes[trimmedKey];
  const lowerKey = trimmedKey.toLowerCase();
  const caseMatch = Object.keys(attributes).find((k) => k.trim().toLowerCase() === lowerKey);
  if (caseMatch !== undefined) return attributes[caseMatch];
  const normalize = (s) => s.trim().toLowerCase().replace(/[\r\n]+/g, " ").replace(/\s+/g, " ");
  const normSearch = normalize(key);
  const prefixMatch = Object.keys(attributes).find((k) => {
    const normCandidate = normalize(k);
    return normSearch.startsWith(normCandidate) || normCandidate.startsWith(normSearch);
  });
  return prefixMatch !== undefined ? attributes[prefixMatch] : undefined;
}

// Format base-response content for a single LLM tab in the modal
function formatCoreAttributesContent(baseResponsesData, brandName) {
  if (!baseResponsesData || !Array.isArray(baseResponsesData)) return "No data available.";
  const brandData = baseResponsesData.find(
    (item) => item?.brandName?.toLowerCase() === brandName?.toLowerCase()
  );
  if (!brandData) return `No data available for ${brandName}.`;

  let content = "";
  if (brandData.basePrompt) {
    content = `### Base Prompt\n\n${brandData.basePrompt}`;
  }
  if (brandData.baseResponse && Array.isArray(brandData.baseResponse)) {
    const count = brandData.baseResponse.length;
    const responsesContent = brandData.baseResponse
      .map((item, idx) =>
        count > 1 ? `**Response ${idx + 1}:**\n\n${item.baseRes || ""}` : item.baseRes || ""
      )
      .join("\n\n---\n\n");
    content += content
      ? `\n\n---\n\n### Base Responses\n\n${responsesContent}`
      : `### Base Responses\n\n${responsesContent}`;
  }
  return content || "No data available.";
}

/**
 * Composite Brand Pillars table — shown when "Compare Across → By LLM" is active in CategoryAnalysis.
 * Averages scores from all selected LLMs for each attribute+brand, with per-LLM tooltip explanations.
 */
export default function CACorePillarsComposite({ category, ctx, multiLLMs }) {
  const _ctx = ctx;
  const { user } = useContext(AuthContext);
  const isCompanyUser = user?.user_role?.role?.name === "Company user";

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [tabbedModal, setTabbedModal] = useState({ open: false, title: "", tabs: [] });

  const llms = (multiLLMs || []).filter(Boolean);
  const primaryMarket = _ctx.markets[0];
  const selectedPersona = _ctx.state.selectedPersona;
  const transactionStatus = _ctx.state.transactionStatus;

  const _defaultComps = _ctx.competitors?.["default"] ?? [];
  const categoryComps = _ctx.competitors?.[category] ?? [];
  const headers = [
    ..._defaultComps.map((item) => item.name),
    ...categoryComps.map((item) => item.name),
  ];

  const totalBrandColumns = _defaultComps.length + categoryComps.length;

  // ── Compute composite data ──────────────────────────────────────────────────

  // Gather core_attributes data from all LLMs
  const allLLMData = llms.map((llm) => ({
    llm,
    data: _ctx.core_attributes?.[llm]?.[category]?.[primaryMarket]?.[selectedPersona],
  })).filter((d) => d.data?.summary && Object.keys(d.data.category_averages || {}).length > 0);

  let loading = true;
  let hasError = false;

  if (allLLMData.length > 0) {
    loading = false;
  } else if (transactionStatus === "RETRY") {
    hasError = true;
    loading = false;
  } else if (transactionStatus === "RUNNING") {
    hasError = false;
    loading = true;
  } else {
    loading = false;
  }

  // Collect union of all attribute names across LLMs
  const attrNameSet = new Set();
  allLLMData.forEach(({ data }) => {
    Object.keys(data.category_averages || {}).forEach((attr) => attrNameSet.add(attr));
  });
  const allAttrNames = [...attrNameSet];

  // For each attribute and brand, average scores across LLMs
  const compositeRows = allAttrNames.map((attrName) => {
    const attDisplayName = formatName(attrName);

    // Collect definition from first LLM that has it
    let definition = "";
    for (const { data } of allLLMData) {
      const firstProduct = data.products?.[0];
      const attrData = findAttrData(firstProduct?.attributes, attrName);
      if (attrData?.definition) { definition = attrData.definition; break; }
    }

    const scores = headers.map((brandName) => {
      // Per-LLM scores and explanations for this attribute + brand
      const perLLMEntries = allLLMData.map(({ llm, data }) => {
        const product = data.products?.find(
          (p) => (p.brandName || p.name)?.toLowerCase() === brandName.toLowerCase()
        );
        const attrData = product ? findAttrData(product.attributes, attrName) : undefined;
        return {
          llm,
          score: attrData?.score,
          explanation: attrData?.explanation,
          multiPromptRuns: Array.isArray(attrData?.multi_prompt_runs) ? attrData.multi_prompt_runs : null,
        };
      });

      const allRunScores = [];
      perLLMEntries.forEach(({ score, multiPromptRuns }) => {
        const runScores = multiPromptRuns
          ? multiPromptRuns
              .map((run) => run?.score ?? run?.rating)
              .filter((v) => v != null && v !== "")
              .map(Number)
          : [];
        if (runScores.length > 0) {
          allRunScores.push(...runScores);
        } else if (score != null && score !== "") {
          allRunScores.push(Number(score));
        }
      });
      const avgScore = allRunScores.length > 0
        ? allRunScores.reduce((sum, s) => sum + s, 0) / allRunScores.length
        : null;

      return {
        name: brandName,
        score: avgScore !== null ? Math.round(avgScore) : null,
        perLLM: perLLMEntries.map(({ llm, score, explanation }) => ({ llm, score, explanation })),
      };
    });

    return { name: attrName, displayName: attDisplayName, definition, scores };
  });

  // ── Per-LLM detail tab helpers ────────────────────────────────────────────

  // Build the row data for one LLM in the same shape DetailedTableModal expects
  function buildLLMTableData(llmData) {
    if (!llmData?.summary) return [];
    return Object.keys(llmData.category_averages || {}).map((attrName) => {
      const firstProduct = llmData.products?.[0];
      const definition = findAttrData(firstProduct?.attributes, attrName)?.definition;
      const scores = headers.map((brandName) => {
        const product = llmData.products?.find(
          (p) => (p.brandName || p.name)?.toLowerCase() === brandName.toLowerCase()
        );
        const attrData = product ? findAttrData(product.attributes, attrName) : undefined;
        return {
          name: brandName,
          score: attrData?.score ?? null,
          explanation: attrData?.explanation ?? "",
        };
      });
      return { name: attrName, definition, scores };
    });
  }

  // Plain header renderer for the detail modal — no info icons, no modal toggle
  function renderDetailHeaders() {
    const headerDefs = [
      { id: "attr", name: ENTITY_LABELS.brandPillar },
      ..._defaultComps.map((item) => ({ id: item.name, name: item.name })),
      ...categoryComps.map((item) => ({ id: item.name, name: item.name })),
    ];
    return (
      <TableHead className={classes.table_head}>
        <TableRow>
          {headerDefs.map((header, index) => {
            const ctxCompetitor = index === 0 ? null
              : index <= _defaultComps.length
                ? _defaultComps[index - 1]
                : categoryComps[index - 1 - _defaultComps.length];
            const domain = ctxCompetitor?.domain ? extractDomain(ctxCompetitor.domain) : "";
            return (
              <TableCell
                key={index}
                className={index === 0 ? classes.table_head_cell_index : classes.table_head_cell}
              >
                <div className={classes.table_head_content}>
                  {index !== 0 ? (
                    <>
                      <BrandAvatar domainName={domain} brandName={header.name} />
                      <div className={classes.table_head_text}>{header.name}</div>
                    </>
                  ) : (
                    <div className={classes.table_head_text_comp}>
                      <span>{header.name}</span>
                    </div>
                  )}
                </div>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  // ── Render helpers ─────────────────────────────────────────────────────────

  function openBaseResponseModal(brandName) {
    const tabs = allLLMData.map(({ llm, data }) => ({
      label: getLLMLabel(llm),
      content: formatCoreAttributesContent(data.baseResponses, brandName),
    }));
    setTabbedModal({
      open: true,
      title: `${ENTITY_LABELS.brandPillar} - ${brandName}`,
      tabs,
    });
  }

  function renderHeaders() {
    const headerDefs = [
      { id: "attr", name: ENTITY_LABELS.brandPillar },
      ..._defaultComps.map((item) => ({ id: item.name, name: item.name })),
      ...categoryComps.map((item) => ({ id: item.name, name: item.name })),
    ];

    return (
      <TableHead className={classes.table_head}>
        <TableRow>
          {headerDefs.map((header, index) => {
            let ctxCompetitor = null;
            if (index !== 0) {
              if (index <= _defaultComps.length) {
                ctxCompetitor = _defaultComps[index - 1];
              } else {
                ctxCompetitor = categoryComps[index - 1 - _defaultComps.length];
              }
            }
            const domain = ctxCompetitor?.domain ? extractDomain(ctxCompetitor.domain) : "";
            const hasBaseResponses = allLLMData.some(
              ({ data }) => Array.isArray(data.baseResponses) && data.baseResponses.length > 0
            );

            return (
              <TableCell
                key={index}
                className={index === 0 ? classes.table_head_cell_index : classes.table_head_cell}
              >
                <div className={classes.table_head_content}>
                  {index !== 0 ? (
                    <>
                      <div className={classes.logo_container}>
                        <BrandAvatar domainName={domain} brandName={header.name} />
                        {!isCompanyUser && hasBaseResponses && (
                          <div className={classes.info_icon_wrapper}>
                            <Tooltip title="View Base Prompts (All LLMs)" arrow placement="top">
                              <IconButton
                                size="small"
                                onClick={() => openBaseResponseModal(header.name)}
                                sx={{
                                  padding: "1px",
                                  backgroundColor: "rgba(255,255,255,0.95)",
                                  color: "rgba(0,0,0,0.54)",
                                  border: "1px solid rgba(0,0,0,0.08)",
                                  "&:hover": {
                                    backgroundColor: "#2196F3",
                                    color: "#fff",
                                    border: "1px solid #2196F3",
                                  },
                                  boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                                }}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: "13px" }} />
                              </IconButton>
                            </Tooltip>
                          </div>
                        )}
                      </div>
                      <div className={classes.table_head_text}>{header.name}</div>
                    </>
                  ) : (
                    <div className={classes.table_head_text_comp}>
                      <div className={classes.table_head_title}>
                        <span>{header.name}</span>
                        {!showDetailModal && (
                          <MUIIconButton onClick={() => setShowDetailModal(true)}>
                            <InfoOutlined />
                          </MUIIconButton>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  function renderRows(rowItem, index) {
    return (
      <StyledTableRow key={index}>
        <Tooltip title={rowItem.definition || ""}>
          <StyledTableCell className={classes.attribute_headings}>
            {rowItem.displayName}
          </StyledTableCell>
        </Tooltip>
        {rowItem.scores.map((scoreObj, idx) => {
          const hasScore = scoreObj.score !== null && scoreObj.score !== undefined;
          let color = "#dc3545";
          if (!hasScore) { color = "#000"; }
          else if (scoreObj.score >= 9) { color = "#198754"; }
          else if (scoreObj.score >= 7) { color = "#2196F3"; }

          // Build per-LLM tooltip content
          const tooltipContent = (
            <div style={{ minWidth: 160 }}>
              {scoreObj.perLLM.map(({ llm, score, explanation }, li) => (
                <div key={li} style={{ marginBottom: li < scoreObj.perLLM.length - 1 ? 8 : 0 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.75rem", color: "#a8c4f0", marginBottom: 2 }}>
                    {getLLMLabel(llm)}
                    {score != null ? ` — ${Number(score).toFixed(0)}` : " — N/A"}
                  </div>
                  {explanation && (
                    <div style={{ fontSize: "0.72rem", opacity: 0.9, lineHeight: 1.4 }}>
                      {explanation}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );

          const cell = (
            <StyledTableCell
              key={idx}
              style={{ color }}
              className={classes.attribute_score}
            >
              {hasScore ? scoreObj.score : "-"}
            </StyledTableCell>
          );

          return hasScore ? (
            <Tooltip
              key={idx}
              title={tooltipContent}
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#1e2340",
                    fontSize: "0.73rem",
                    borderRadius: "10px",
                    padding: "9px 12px",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.28)",
                    "& .MuiTooltip-arrow": { color: "#1e2340" },
                  },
                },
              }}
              slotProps={{ popper: { modifiers: [{ name: "offset", options: { offset: [0, -18] } }] } }}
            >
              {cell}
            </Tooltip>
          ) : (
            cell
          );
        })}
      </StyledTableRow>
    );
  }

  function LoadingSkeleton() {
    return (
      <div className="p-3">
        <Skeleton variant="rectangular" height={30} />
        {Array(8).fill(0).map((_, i) => (
          <Skeleton key={i} variant="rectangular" width={i % 2 === 0 ? "80%" : "97%"} className="my-3" height={20} />
        ))}
      </div>
    );
  }

  // Map compositeRows to the shape Table component expects
  const tableData = compositeRows.map((row) => ({
    ...row,
    // The Table component calls render(rowItem, index)
  }));

  return (
    <>
      <MUICard variant="elevated" elevation={0} className={classes.card}>
        {allLLMData.length > 0 && !loading && !hasError && compositeRows.length > 0 && (
          <>
            <div className={classes.table_scroll_wrapper}>
              <div style={{ minWidth: `calc(200px + ${totalBrandColumns} * 130px)` }}>
                <Table
                  elevation={0}
                  data={tableData}
                  renderHeaders={() => renderHeaders()}
                  render={renderRows}
                />
              </div>
            </div>

            <CADetailedTableModal
              show={showDetailModal}
              onClose={() => setShowDetailModal(false)}
              tabs={allLLMData.map(({ llm, data }) => ({
                label: getLLMLabel(llm),
                data: buildLLMTableData(data),
                headers,
                renderHeaders: renderDetailHeaders,
                formatName,
                pdfTitle: `${ENTITY_LABELS.brandPillar} - ${getLLMLabel(llm)}`,
                classes,
              }))}
            />
          </>
        )}
        {loading && !hasError && <LoadingSkeleton />}
        {!loading && !hasError && allLLMData.length === 0 && (
          <div style={{ padding: 16, color: "#888", fontSize: "0.9rem" }}>
            No data available across selected LLMs.
          </div>
        )}
      </MUICard>

      <CATabbedModal
        open={tabbedModal.open}
        title={tabbedModal.title}
        tabs={tabbedModal.tabs}
        onClose={() => setTabbedModal((s) => ({ ...s, open: false }))}
      />
    </>
  );
}
